import { OverlayContainer } from "@angular/cdk/overlay";
import {
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  effect,
  signal,
  untracked,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from "@angular/router";
import {
  APP_AUTH_STATE,
  AlertMessage,
  ObjectDebuggerComponent,
  checkIfApplicationIsLoadedInsideAnIframe,
} from "blu-utilities";
import { Observable, Subject, Subscription, forkJoin } from "rxjs";
import { filter, finalize } from "rxjs/operators";
import { BaseComponent } from "src/app/components/base/base.component";
import { PageLoadingComponent } from "src/app/components/page-loading/page-loading.component";
import { XModalComponent } from "src/app/components/x-modal/x-modal.component";
import { AuthenticationService } from "src/app/modules/authentication/services/authentication.service";
import { BroadcastMessages } from "src/app/shared/general/constants";
import { environment } from "src/environments/environment";
import { AppService } from "src/services/app.service";
import { AddClassModifierDirective } from "./core/directives/add-class-modifier.directive";
import { COOKIES } from "./core/models/cookie";
import { ErrorResponse } from "./core/models/error";
import { PAGE_URLS } from "./core/models/general";
import { ActivityService } from "./core/services/activity.service";
import { AppNotificationService } from "./core/services/app-notification.service";
import { ColorModeService } from "./core/services/color-mode.service";
import { CommunicationService } from "./core/services/communication.service";
import { LogoutService } from "./core/services/logout.service";
import { PopupNotificationService } from "./core/services/popup-notification.service";
import { SessionTimeoutService } from "./core/services/session-timeout.service";
import { createPopupNotificationForAsyncReport } from "./core/utils/report";
import { AdminApiService } from "./modules/admins/services/admin-api.service";
import { EditPasswordComponent } from "./modules/authentication/edit-password/edit-password.component";
import { RefreshState } from "./modules/authentication/models/login";
import { AccountService } from "./modules/authentication/services/account.service";
import { LeaderElectionService } from "./modules/authentication/services/leader-election.service";
import { REPORT_CODE } from "./modules/reporting/models/constants";
import {
  AsyncReport,
  AsyncReportStatus,
} from "./modules/reporting/reports/async/models/async-report";
import { AsyncReportApiService } from "./modules/reporting/reports/async/services/async-report-api.service";
import { AsyncReportService } from "./modules/reporting/reports/async/services/async-report.service";
import { HeaderComponent } from "./partials/header/header.component";
import { SidebarComponent } from "./partials/sidebar/sidebar.component";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
  imports: [
    AddClassModifierDirective,
    SidebarComponent,
    XModalComponent,
    EditPasswordComponent,
    HeaderComponent,
    RouterOutlet,
    PageLoadingComponent,
    ObjectDebuggerComponent,
  ],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  readonly isWaitingForRefreshTokenFromAnotherTab = signal(false);
  private storageEventListener: (event: StorageEvent) => void;
  readonly shouldShowObjectDebugger =
    environment.debuggingOptions?.showObjectDebugger;
  asyncReportSubscription$?: Subscription;
  intervalForAsyncReportChecking: any;
  isScreenPositionNotOnTop = false;
  title = "blu-platform-angular";
  isLoadingAsyncReport = false;

  isObjectDebuggerVisible = this.appService.getIsObjectDebuggerVisible;
  isEditPasswordModalOpen = this.appService.getIsEditPasswordModalOpen;
  isSideNavBarVisible = this.appService.getIsSideNavBarVisible;
  isSideBarCollapsed = this.appService.isSideBarCollapsed;
  isAdminAuthenticated = this.accountService.isLoggedIn;

  get isDarkMode() {
    return this.colorModeService.isDarkMode;
  }

  readonly isAdminAuthenticationValid = computed(
    () =>
      this.isAdminAuthenticated() &&
      this.authenticationService.getAuthenticatedAdminData(),
  );

  readonly isApplicationLoading = computed(() => {
    return (
      this.appService.getIsApplicationLoading() ||
      this.appService.getIsAuthGuardNavigationPending() ||
      this.appService.getIsFetchingAuthenticatedAdmin() ||
      this.appService.getIsLoggingInWithOAuth() ||
      this.isWaitingForRefreshTokenFromAnotherTab()
    );
  });

  constructor(
    private popupNotificationService: PopupNotificationService,
    private sessionTimeoutService: SessionTimeoutService,
    private authenticationService: AuthenticationService,
    private leaderElectionService: LeaderElectionService,
    private asyncReportApiService: AsyncReportApiService,
    private communicationService: CommunicationService,
    appNotificationService: AppNotificationService,
    private asyncReportService: AsyncReportService,
    private colorModeService: ColorModeService,
    private overlayContainer: OverlayContainer,
    private adminApiService: AdminApiService,
    private activityService: ActivityService,
    private accountService: AccountService,
    private logoutService: LogoutService,
    public alertSnackBar: MatSnackBar,
    private appService: AppService,
    private destroyRef: DestroyRef,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super(appNotificationService);
    this.subscribeToBroadcast();
    this.setLoadingAndBroadcastMessage();
    // Bind the method to ensure 'this' context is preserved when called as an event listener.
    this.storageEventListener = (event: StorageEvent) => {
      this.handleStorageEvent(event);
    };

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.checkReturnUrl();
        }

        if (event instanceof NavigationError) {
          this.handleNavigationError(event);
        }
      });

    this.listenForAuthChange();
    this.listenToRefreshTokenState();
    this.subscribeToNotifications();
    this.checkLoggedInObservable();
    this.initializeActivityMonitor();
    this.syncDarkModeToOverlayContainer();
  }

  /**
   * The owl-date-time popup renders inside the CDK overlay container on the body,
   * outside `.app_is-dark`, so component level dark styles cannot reach it.
   * Mirror the color mode onto the overlay container instead — see src/styles/owl.scss.
   */
  private syncDarkModeToOverlayContainer() {
    const overlayContainerElement = this.overlayContainer.getContainerElement();
    effect(() => {
      overlayContainerElement.classList.toggle(
        "app-overlay_is-dark",
        this.colorModeService.isDarkModeSignal(),
      );
    });
  }

  initializeActivityMonitor() {
    effect(() => {
      if (this.accountService.isLoggedIn()) {
        untracked(() => {
          this.activityService.initialize();
          this.sessionTimeoutService.initializeResumeChecks();
        });
      }
    });
  }

  /**
   * Handles navigation errors from lazy-loaded modules.
   * Alerts if offline, or prompts to reload if a new version is available.
   *
   * @param event The navigation error event.
   */
  private handleNavigationError(event: NavigationError): void {
    // Covers Chrome, Firefox, Safari import/chunk errors
    const chunkLoadErrorRegex =
      /(Failed to fetch dynamically|Error loading dynamically|Failed to load module)/i;

    if (event.error && chunkLoadErrorRegex.test(event.error.message)) {
      if (!navigator.onLine) {
        this.getNotificationService().showAlert(
          "You appear to be offline. Please check your connection.",
        );
      } else if (confirm("A new version is available. Reload now?")) {
        // Handle the chunk load failure, reload the current page
        window.location.assign(event.url);
      }
    }
  }

  checkLoggedInObservable() {
    this.appService
      .getLoggedInObservable()
      .pipe(filter((value) => value)) // make sure the logged In state is true
      .subscribe(() => {
        this.listenForNotificationChange();
        this.appService.navigateToReturnUrl();
        // get admin user data after user logged in (normal login flow OR after get TOKEN_FROM_ANOTHER_TAB)
        this.getAuthenticatedAdmin();
      });
  }

  ngOnInit() {
    checkIfApplicationIsLoadedInsideAnIframe(PAGE_URLS, "flex");
    this.listenForNotificationChange();
  }

  /**
   * when the component first loads we don't have a token
   * before manually sending refresh token request we should try to get
   * a token from other open tabs
   */
  setLoadingAndBroadcastMessage() {
    this.isWaitingForRefreshTokenFromAnotherTab.set(true);
    this.communicationService.sendMessage({
      type: BroadcastMessages.NEW_WINDOW_OPENED,
    });
  }

  subscribeToNotifications() {
    this.subscribeToNotificationSubject(
      "_is-success",
      this.getNotificationService().getSuccessMessage(),
    );
    this.subscribeToNotificationSubject(
      "_is-error",
      this.getNotificationService().getErrorMessage(),
    );
    this.subscribeToNotificationSubject(
      "_is-alert",
      this.getNotificationService().getAlertMessage(),
    );
  }

  subscribeToNotificationSubject(
    modifier: string,
    subject$: Subject<AlertMessage>,
  ) {
    const panelClass = ["snack-bar", `snack-bar${modifier}`];
    subject$.subscribe(({ message, duration }) => {
      this.alertSnackBar.open(message, "Close", {
        duration,
        panelClass,
        verticalPosition: "top",
      });
    });
  }

  listenForAuthChange() {
    window.addEventListener("storage", this.storageEventListener);
  }

  /**
   * Handles the storage event for authentication changes.
   *
   * This method is triggered by a 'storage' event listener set up elsewhere in the service.
   * It listens for changes to the 'localStorage' that indicate a change in the authentication
   * state across tabs/windows of the same origin. When a change is detected, it takes appropriate
   * action based on whether the event signifies a login or logout.
   *
   * @param {StorageEvent} event - The storage event triggered when there's a change in localStorage.
   */
  private handleStorageEvent(event: StorageEvent): void {
    // Check if the changed item is the authentication event key.
    if (event.key === COOKIES.AUTH_EVENT) {
      // If a logout event is detected, invoke the logout service to handle the logout process
      if (event.newValue === APP_AUTH_STATE.LOGGED_OUT.toString()) {
        this.logoutService.removeSessionAndRedirectToLogin({
          skipReturnUrl: true,
        });
      }
    }
  }

  listenToRefreshTokenState(): void {
    this.leaderElectionService.refreshState$.subscribe((state) => {
      if (state === RefreshState.STARTED) {
        // Show loading indicator
      } else if (state === RefreshState.SUCCESS) {
        // Hide loading indicator, show success message
        this.handleRefreshSuccess();
      } else if (
        state &&
        [RefreshState.FAILED, RefreshState.SKIPPED].includes(state)
      ) {
        // Show error message
        this.resetLoadingState();
      }
    });
  }

  private handleRefreshSuccess(): void {
    this.resetLoadingState();
    this.appService.navigateToReturnUrl();
    // get admin data after success login using refresh token flow
    this.getAuthenticatedAdmin();
  }

  private resetLoadingState() {
    this.isWaitingForRefreshTokenFromAnotherTab.set(false);
  }

  listenForNotificationChange() {
    // Clear existing interval if it exists
    if (this.intervalForAsyncReportChecking) {
      clearInterval(this.intervalForAsyncReportChecking);
    }

    // Create a new interval
    this.intervalForAsyncReportChecking = setInterval(() => {
      this.checkForAsyncReports();
    }, 2000);
  }

  /**
   * Checks for any pending async reports and processes them accordingly.
   * It retrieves pending reports from storage using the asyncReportService.
   * If there are pending reports, it iterates through each report and handles them based on their status.
   * If a report has status AsyncReportStatus.GENERATED, it adds a popup notification using the popupNotificationService.
   * If a report is not being generated, it adds a request to fetch the report's status using the getPendingReportStatus method.
   * After processing the pending reports, it marks filtered notifications as read.
   * If there are no pending reports, it clears all notifications using the popupNotificationService.
   */
  checkForAsyncReports() {
    if (!this.isAdminAuthenticated()) {
      clearInterval(this.intervalForAsyncReportChecking);
      return;
    }

    const reportBeingGenerated =
      this.asyncReportService.getReportBeingGenerated();
    const pendingReportsFromStorage =
      this.asyncReportService.getPendingReportsForAdmin();
    if (pendingReportsFromStorage?.length) {
      const requests: Observable<any>[] = [];
      pendingReportsFromStorage.forEach((report) => {
        if (
          report.status === AsyncReportStatus.GENERATED &&
          reportBeingGenerated !== report.code
        ) {
          this.popupNotificationService.addPopupNotification(
            createPopupNotificationForAsyncReport(
              report,
              this.asyncReportService,
            ),
          );
        } else if (!reportBeingGenerated) {
          requests.push(this.getPendingReportStatus(report.code));
        }
      });
      if (requests.length) {
        if (!this.isLoadingAsyncReport) {
          this.isLoadingAsyncReport = true;
          this.asyncReportSubscription$ = forkJoin(requests).subscribe({
            next: (generatedReports: AsyncReport[]) => {
              this.isLoadingAsyncReport = false;
              if (generatedReports?.length) {
                generatedReports.forEach((report) => {
                  this.createNotificationForReport(report);
                });
              }
              this.markFilteredNotificationsAsRead();
            },
            error: () => {
              this.isLoadingAsyncReport = false;
            },
          });
        }
      } else {
        this.markFilteredNotificationsAsRead();
      }
    } else {
      // no pending reports => we clear the notifications
      this.popupNotificationService.clearNotifications();
    }
  }

  getPendingReportStatus(code: REPORT_CODE): Observable<AsyncReport | null> {
    return this.asyncReportApiService.getPendingReportStatus(code);
  }

  markFilteredNotificationsAsRead() {
    const pendingReportsFromStorage =
      this.asyncReportService.getPendingReportsForAdmin();
    const filteredNotifications = this.popupNotificationService
      .getNotifications()
      .filter(
        (notification) =>
          !pendingReportsFromStorage.find(
            (pendingReport) => pendingReport.id === notification.id,
          ),
      );
    filteredNotifications.forEach((notification) => {
      this.popupNotificationService.markNotificationAsReadById(notification.id);
    });
  }

  createNotificationForReport(report: AsyncReport) {
    if (report.status === AsyncReportStatus.GENERATED) {
      this.popupNotificationService.addPopupNotification(
        createPopupNotificationForAsyncReport(report, this.asyncReportService),
      );
    } else if (report.status === AsyncReportStatus.FAILED) {
      this.getNotificationService().showError("Report failed");
    }
  }

  closeEditPasswordModal() {
    this.appService.setIsEditPasswordModalOpen(false);
  }

  /**
   * check if there is a return url
   */
  checkReturnUrl() {
    const returnUrl =
      this.route.snapshot.queryParams["returnUrl"] ?? this.router.url;
    this.appService.setReturnUrl(returnUrl);
  }

  getAuthenticatedAdmin() {
    if (this.appService.getIsFetchingAuthenticatedAdmin()) {
      return;
    }
    if (
      this.isAdminAuthenticated() &&
      !this.authenticationService.getAuthenticatedAdminData()
    ) {
      this.setAdminAuthenticationFlags(true);
      this.adminApiService
        .getCurrentAuthenticatedAdminData()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.setAdminAuthenticationFlags(false);
          }),
        )
        .subscribe({
          error: (error: ErrorResponse) => {
            this.handleErrorResponse(error);
          },
        });
    }
  }

  setAdminAuthenticationFlags(value: boolean) {
    this.appService.setIsFetchingAuthenticatedAdmin(value);
    this.appService.setIsApplicationLoading(value);
  }

  @HostListener("window:scroll")
  onWindowScroll() {
    const scrollY = window.scrollY || window.pageYOffset;
    this.isScreenPositionNotOnTop = scrollY > 100;
  }

  showObjectDebugger() {
    this.appService.setIsObjectDebuggerVisible(true);
  }

  moveToTop() {
    window.scrollTo(0, 0);
  }

  closeObjectDebugger() {
    this.appService.setIsObjectDebuggerVisible(false);
  }

  subscribeToBroadcast() {
    this.communicationService.subscribeToMessages(({ type, data }) => {
      switch (type) {
        case BroadcastMessages.TOKENS_UPDATED:
          this.accountService.setUserAccessToken(data);
          this.appService.setLoggedInObservable(true);
          break;
        case BroadcastMessages.NEW_WINDOW_OPENED: {
          const token = this.accountService.getUserAccessToken();
          this.communicationService.sendMessage({
            type: BroadcastMessages.TOKEN_FROM_ANOTHER_TAB,
            data: token,
          });
          break;
        }
        case BroadcastMessages.TOKEN_FROM_ANOTHER_TAB:
          // Replies to NEW_WINDOW_OPENED reach every open tab, not just the
          // tab that asked — only apply them while this tab is still waiting.
          if (data && this.isWaitingForRefreshTokenFromAnotherTab()) {
            this.accountService.setUserAccessToken(data);
            this.appService.setLoggedInObservable(true);
          }
          this.isWaitingForRefreshTokenFromAnotherTab.set(false);
          break;
        case BroadcastMessages.COLOR_MODE_UPDATED:
          if (data) {
            this.colorModeService.setColorMode(data);
          }
          break;
        default:
          break;
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener("storage", this.storageEventListener);
    clearInterval(this.intervalForAsyncReportChecking);
  }
}
